import { BaseComponent } from '../core/BaseComponent.js';
import { showToast } from '../utils/toast.js';

export class Facility extends BaseComponent {
    constructor(selector = '#ds_facility_modal', options = {}) {
        super(selector, options);

        this.facilitiesTable = options.facilitiesTable || null;
        this.ajaxUrl = window.docuswiftPortal?.ajax_url || '/wp-admin/admin-ajax.php';

        this._activeFacilityId = null;
        this._activeDepartmentId = null;
        this.activeRequestId = null;
        this._addingNewDepartment = false;
    }

    get activeFacilityId() { return this._activeFacilityId; }
    set activeFacilityId(value) {
        this._activeFacilityId = value;
        this.updateApplyButtonState();
    }

    get activeDepartmentId() { return this._activeDepartmentId; }
    set activeDepartmentId(value) {
        this._activeDepartmentId = value;
        this.updateApplyButtonState();
    }

    /**
     * Set the request this modal should apply changes to.
     * When set, the "Apply to Request" button becomes enabled once a facility
     * is open and a department is active. Pass null to clear.
     */
    setActiveRequest(requestId) {
        this.activeRequestId = requestId ? parseInt(requestId, 10) || null : null;
        this.updateApplyButtonState();
    }

    init() {
        if (!super.init()) return this;

        this.cacheElements();
        this.bindEvents();

        return this;
    }

    cacheElements() {
        this.modal = document.getElementById('ds_facility_modal') || this.element;
        this.overlay = this.modal?.querySelector('.overlay');
        this.closeBtn = this.modal?.querySelector('.close_modal');
        this.backBtn = this.modal?.querySelector('.back_facility_modal_btn');

        this.facilityMainCol = this.modal?.querySelector('.facility-form-main');
        this.facilityEditBtn = this.modal?.querySelector('.facility-main-edit-btn');
        this.facilityCancelBtn = this.modal?.querySelector('.facility-main-cancel-btn');

        this.facilityForm = document.getElementById('facility-main-form');
        this.facilityIdInput = this.facilityForm?.querySelector('[name="facility_id"]');

        this.departmentsList = this.modal?.querySelector('.departments-list');
        this.addDeptBtn = this.modal?.querySelector('.add-department-btn');
        this.deptsEmpty = this.modal?.querySelector('.departments-empty');

        this.deptDetailCol = this.modal?.querySelector('.facility-form-department-detail');
        this.deptDetailContent = this.modal?.querySelector('.department-detail-content');
        this.deptEditBtn = this.modal?.querySelector('.department-edit-btn');
        this.deptCancelBtn = this.modal?.querySelector('.department-cancel-btn');

        this.deptForm = document.getElementById('ds_department_form');
        this.deptIdInput = this.deptForm?.querySelector('[name="department_id"]');
        this.parentIdInput = this.deptForm?.querySelector('[name="parent_facility_id"]');
        this.deptEmpty = this.modal?.querySelector('.department-empty');

        this.saveLoader = document.getElementById('facility_modal_save_loader');

        this.applyBtn = document.getElementById('apply_facility_to_request_btn');

        this.openButton = document.querySelector('#add-new-facility');
        this.addNewFacilityBtn = this.modal?.querySelector('#add_new_facility_btn');

        if (!this.deptForm) {
            console.warn('Facility: #ds_department_form not found in DOM');
        }
    }

    /**
     * Lock/unlock editable controls; readonly view uses disabled + opacity (see SCSS .facility-form--fields-locked).
     */
    setFormControlsDisabled(form, disabled) {
        if (!form) return;
        form.querySelectorAll('input:not([type="hidden"]), select, textarea').forEach((el) => {
            el.disabled = disabled;
        });
        form.classList.toggle('facility-form--fields-locked', disabled);
    }

    setFacilityMode(mode) {
        if (this.facilityMainCol) this.facilityMainCol.dataset.facilityMode = mode;
        this.applyFacilityFieldState();
    }

    applyFacilityFieldState() {
        const mode = this.facilityMainCol?.dataset.facilityMode;
        if (!this.facilityForm) return;
        this.setFormControlsDisabled(this.facilityForm, mode === 'readonly');
    }

    setDeptMode(mode) {
        if (this.deptDetailCol) this.deptDetailCol.dataset.deptMode = mode;
        this.applyDepartmentFieldState();
    }

    applyDepartmentFieldState() {
        const mode = this.deptDetailCol?.dataset.deptMode;
        const form = document.getElementById('ds_department_form');
        if (!form) return;
        if (mode === 'empty') {
            this.setFormControlsDisabled(form, false);
            return;
        }
        this.setFormControlsDisabled(form, mode === 'readonly');
    }

    bindEvents() {
        this.openButton?.addEventListener('click', (e) => {
            e.preventDefault();
            this.openNew();
        });

        this.addNewFacilityBtn?.addEventListener('click', (e) => {
            e.preventDefault();
            this.openNew();
        });

        document.addEventListener('click', (e) => {
            const editBtn = e.target.closest('#facility_edit_btn, .facility-edit-btn');
            if (!editBtn) return;
            e.preventDefault();
            const id = editBtn.dataset.facilityId;
            if (id) this.openEdit(parseInt(id, 10));
        });

        this.closeBtn?.addEventListener('click', (e) => {
            e.preventDefault();
            this.close();
        });
        this.backBtn?.addEventListener('click', (e) => {
            e.preventDefault();
            this.close();
        });
        this.overlay?.addEventListener('click', () => this.close());

        this.facilityForm?.addEventListener('submit', (e) => this.handleSaveFacility(e));

        this.facilityEditBtn?.addEventListener('click', () => this.enterFacilityEditMode());
        this.facilityCancelBtn?.addEventListener('click', () => this.cancelFacilityEdit());

        this.addDeptBtn?.addEventListener('click', () => this.showNewDepartmentForm());

        this.deptEditBtn?.addEventListener('click', () => this.enterDepartmentEditMode());
        this.deptCancelBtn?.addEventListener('click', () => this.cancelDepartmentEdit());

        this.modal?.addEventListener('submit', (e) => {
            if (e.target && e.target.id === 'ds_department_form') {
                this.handleSaveDepartment(e);
            }
        });

        // Department "current file" card: flag the existing server-side file
        // for removal when the user clicks its remove button, then hide the
        // card so the upload dropzone is the only visible file control again.
        this.modal?.addEventListener('click', (e) => {
            const removeBtn = e.target.closest('[data-dept-file-remove]');
            if (!removeBtn) return;
            e.preventDefault();
            const form = removeBtn.closest('#ds_department_form');
            if (!form) return;
            const currentFile = form.querySelector('[data-dept-current-file]');
            const removeFlag = form.querySelector('[name="dept_file_remove"]');
            if (removeFlag && currentFile?.dataset.deptFileExistingId) {
                removeFlag.value = '1';
            }
            if (currentFile) {
                currentFile.hidden = true;
                delete currentFile.dataset.deptFileExistingId;
            }
        });

        this.modal?.addEventListener('change', (e) => {
            if (!e.target.matches?.('#ds_department_form [name="dept_file"]')) return;
            const form = e.target.closest('#ds_department_form');
            const removeFlag = form?.querySelector('[name="dept_file_remove"]');
            // Picking a new file replaces the existing one — don't also send remove.
            if (removeFlag) removeFlag.value = '';
        });

        this.applyBtn?.addEventListener('click', (e) => {
            e.preventDefault();
            this.applyToRequest();
        });
    }

    /**
     * Enable the "Apply to Request" button only when we have all three:
     * a request id, an open facility, and an active department.
     */
    updateApplyButtonState() {
        if (!this.applyBtn) return;
        const ready = Boolean(this.activeRequestId && this.activeFacilityId && this.activeDepartmentId);
        this.applyBtn.disabled = !ready;
    }

    /**
     * Persist the currently open facility + active department on the active
     * request. Fires `docuswift:request-facility-applied` on success so
     * RequestDetails can close its modals and refresh.
     */
    async applyToRequest() {
        if (!this.activeRequestId || !this.activeFacilityId || !this.activeDepartmentId) return;

        const btn = this.applyBtn;
        const prevLabel = btn ? btn.textContent : null;
        if (btn) { btn.disabled = true; btn.classList.add('loading'); btn.textContent = 'Applying…'; }

        try {
            const data = await this.ajax('docuswift_apply_facility_to_request', {
                request_id: this.activeRequestId,
                facility_id: this.activeFacilityId,
                department_id: this.activeDepartmentId,
                nonce: window.docuswiftPortal?.nonce || '',
            });
            document.dispatchEvent(new CustomEvent('docuswift:request-facility-applied', {
                detail: {
                    requestId: this.activeRequestId,
                    facilityId: this.activeFacilityId,
                    departmentId: this.activeDepartmentId,
                    data,
                },
            }));
            this.close();
        } catch (err) {
            console.error('Apply to request failed:', err);
            window.alert(err?.message || 'Could not apply facility to the request.');
        } finally {
            if (btn) { btn.classList.remove('loading'); btn.textContent = prevLabel || 'Apply to Request'; }
            this.updateApplyButtonState();
        }
    }

    enterFacilityEditMode() {
        this.setFacilityMode('edit');
    }

    async cancelFacilityEdit() {
        if (!this.activeFacilityId) return;
        this.facilityForm?.classList.add('loading');
        try {
            const data = await this.ajax('get_facility', { facility_id: this.activeFacilityId });
            this.populateForm(this.facilityForm, data);
            this.setFacilityMode('readonly');
        } catch (err) {
            console.error('Failed to reload facility:', err);
        } finally {
            this.facilityForm?.classList.remove('loading');
        }
    }

    enterDepartmentEditMode() {
        this.setDeptMode('edit');
    }

    async cancelDepartmentEdit() {
        const id = this.activeDepartmentId;
        if (!id) return;
        const form = document.getElementById('ds_department_form');
        if (!form) return;
        form.classList.add('loading');
        try {
            const data = await this.ajax('get_department', { department_id: id });
            this.populateForm(form, data);
            this.setDeptMode('readonly');
        } catch (err) {
            console.error('Failed to reload department:', err);
        } finally {
            form.classList.remove('loading');
        }
    }

    openNew() {
        this.resetAll();
        this.show();
    }

    async openEdit(facilityId) {
        this.resetAll();
        this.activeFacilityId = facilityId;
        if (this.facilityIdInput) this.facilityIdInput.value = facilityId;
        // Before show(): avoid one frame of data-facility-mode="new" (Save visible) then switching to readonly.
        this.setFacilityMode('readonly');

        this.show();

        this.facilityForm?.classList.add('loading');

        try {
            const data = await this.ajax('get_facility', { facility_id: facilityId });
            this.populateForm(this.facilityForm, data);
            this.setFacilityMode('readonly');
            this.enableDepartments();
            await this.loadDepartments();
        } catch (err) {
            console.error('Failed to load facility:', err);
        } finally {
            this.facilityForm?.classList.remove('loading');
        }
    }

    close() {
        this.hideSavePreloader();
        if (this.modal) {
            this.modal.classList.remove('active');
            // this.fadeOut(this.modal);
        }
        const url = new URL(window.location.href);
        if (url.searchParams.has('request-id') || url.searchParams.has('request_id')) {
            url.searchParams.delete('request-id');
            url.searchParams.delete('request_id');
            window.history.replaceState({}, '', url.toString());
        }
        this.activeRequestId = null;
        this.updateApplyButtonState();
    }

    showSavePreloader() {
        if (!this.saveLoader) return;
        this.saveLoader.hidden = false;
        this.saveLoader.setAttribute('aria-hidden', 'false');
    }

    hideSavePreloader() {
        if (!this.saveLoader) return;
        this.saveLoader.hidden = true;
        this.saveLoader.setAttribute('aria-hidden', 'true');
    }

    show() {
        if (this.modal) {
            this.modal.classList.add('active');
            // this.fadeIn(this.modal);
        }
    }

    resetAll() {
        this.hideSavePreloader();
        this.activeFacilityId = null;
        this.activeDepartmentId = null;
        this._addingNewDepartment = false;
        this.facilityForm?.reset();
        if (this.facilityIdInput) this.facilityIdInput.value = '';
        this.setFacilityMode('new');
        this.disableDepartments();
        this.hideDepartmentForm();
    }

    async handleSaveFacility(e) {
        e.preventDefault();
        if (!this.facilityForm) return;

        const formData = new FormData(this.facilityForm);
        formData.append('action', 'save_facility');

        try {
            this.showSavePreloader();
            const data = await this.ajaxRaw(formData);
            this.activeFacilityId = data.facility_id;
            if (this.facilityIdInput) this.facilityIdInput.value = data.facility_id;
            this.enableDepartments();
            this._addingNewDepartment = false;

            const fresh = await this.ajax('get_facility', { facility_id: data.facility_id });
            this.populateForm(this.facilityForm, fresh);
            this.setFacilityMode('readonly');

            await this.loadDepartments();
            this.facilitiesTable?.reset();
        } catch (err) {
            alert(err.message || 'Error saving facility');
        } finally {
            this.hideSavePreloader();
        }
    }

    enableDepartments() {
        if (this.addDeptBtn) this.addDeptBtn.disabled = false;
        if (this.deptsEmpty) this.deptsEmpty.style.display = 'none';
    }

    disableDepartments() {
        if (this.addDeptBtn) this.addDeptBtn.disabled = true;
        if (this.departmentsList) {
            this.departmentsList.innerHTML = '<p class="departments-empty">Save a facility first to manage departments.</p>';
            this.deptsEmpty = this.departmentsList.querySelector('.departments-empty');
        }
    }

    async loadDepartments() {
        if (!this.activeFacilityId) return;

        try {
            const data = await this.ajax('get_departments', { facility_id: this.activeFacilityId });
            await this.renderDepartmentsList(data.departments || []);
        } catch (err) {
            console.error('Failed to load departments:', err);
        }
    }

    async renderDepartmentsList(departments) {
        if (!this.departmentsList) return;

        if (!departments.length) {
            this.departmentsList.innerHTML = '<p class="departments-no-items">No departments yet. Use the button below to add one.</p>';
            this.hideDepartmentForm();
            return;
        }

        const activeNum = this.activeDepartmentId != null ? Number(this.activeDepartmentId) : null;

        this.departmentsList.innerHTML = departments.map(dept => `
            <div class="department-item ${Number(dept.id) === activeNum ? 'active' : ''}" data-dept-id="${dept.id}">
                <span class="department-item__name">${dept.type_name ? `${dept.type_name}` : dept.name}</span>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6 12L10 8L6 4" stroke="#717680" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
            </div>
        `).join('');

        this.departmentsList.querySelectorAll('.department-item').forEach(el => {
            el.addEventListener('click', () => {
                this._addingNewDepartment = false;
                const id = parseInt(el.dataset.deptId, 10);
                this.selectDepartment(id);
            });
        });

        if (this._addingNewDepartment) {
            return;
        }

        const ids = departments.map(d => Number(d.id));
        const selectId =
            activeNum != null && ids.includes(activeNum)
                ? activeNum
                : Number(departments[0].id);

        await this.selectDepartment(selectId);
    }

    async selectDepartment(deptId) {
        const form = document.getElementById('ds_department_form');
        if (!form) {
            console.error('Facility: department form missing');
            return;
        }
        this.deptForm = form;
        this.deptIdInput = form.querySelector('[name="department_id"]');
        this.parentIdInput = form.querySelector('[name="parent_facility_id"]');

        this.activeDepartmentId = deptId;

        this.departmentsList?.querySelectorAll('.department-item').forEach(el => {
            el.classList.toggle('active', parseInt(el.dataset.deptId, 10) === deptId);
        });

        this.deptDetailContent?.querySelector('.department-empty')?.remove();
        form.classList.add('loading');

        try {
            const data = await this.ajax('get_department', { department_id: deptId });
            if (this.deptIdInput) this.deptIdInput.value = deptId;
            if (this.parentIdInput) this.parentIdInput.value = this.activeFacilityId;
            this.populateForm(form, data);
            this.renderDeptFile(form, data.file);
            this.setDeptMode('readonly');
        } catch (err) {
            console.error('Failed to load department:', err);
        } finally {
            form.classList.remove('loading');
        }
    }

    /**
     * Render the department file widget based on what's stored server-side.
     *
     * The upload dropzone (.custom-file-upload) is always reset to its empty
     * state here — it is only ever used to pick a NEW file. An already
     * saved file is rendered separately in the `[data-dept-current-file]`
     * card, with its own Preview (via the global DocxPreview `data-docx-preview-*`
     * attributes) and Remove controls.
     *
     * @param {HTMLFormElement} form
     * @param {{id:number,url:string,filename:string}|null} file
     */
    renderDeptFile(form, file) {
        if (!form) return;

        const label     = form.querySelector('[data-dept-file-label]') || form.querySelector('.custom-file-upload');
        const preview   = label?.querySelector('.file-preview');
        const nameEl    = label?.querySelector('[data-dept-file-name]') || label?.querySelector('.file-preview-text-title');
        const typeEl    = label?.querySelector('.filetype-text-title');
        const fileInput = form.querySelector('[name="dept_file"]');
        const removeFlag = form.querySelector('[name="dept_file_remove"]');

        // Always reset the dropzone — it never displays the saved file.
        if (fileInput) fileInput.value = '';
        if (removeFlag) removeFlag.value = '';
        if (nameEl) nameEl.textContent = 'No file selected';
        if (typeEl) typeEl.textContent = 'FILE';
        label?.classList.remove('has-file');
        preview?.classList.remove('has-file');

        const currentFile       = form.querySelector('[data-dept-current-file]');
        const currentExtEl      = currentFile?.querySelector('[data-dept-current-file-ext]');
        const currentNameEl     = currentFile?.querySelector('[data-dept-current-file-name]');
        const currentPreviewBtn = currentFile?.querySelector('[data-dept-current-file-preview]');

        if (file && file.filename) {
            const ext = (file.filename.split('.').pop() || 'FILE').toUpperCase();
            if (currentExtEl) currentExtEl.textContent = ext;
            if (currentNameEl) currentNameEl.textContent = file.filename;
            if (currentPreviewBtn) {
                currentPreviewBtn.dataset.docxPreviewUrl = file.url || '';
                currentPreviewBtn.dataset.docxPreviewName = file.filename;
            }
            if (currentFile) {
                currentFile.hidden = false;
                currentFile.dataset.deptFileExistingId = file.id ? String(file.id) : '';
            }
        } else if (currentFile) {
            currentFile.hidden = true;
            delete currentFile.dataset.deptFileExistingId;
        }
    }

    showNewDepartmentForm() {
        const form = document.getElementById('ds_department_form');
        if (!form) {
            console.error('Facility: department form missing');
            return;
        }
        this.deptForm = form;
        this.deptIdInput = form.querySelector('[name="department_id"]');
        this.parentIdInput = form.querySelector('[name="parent_facility_id"]');

        this._addingNewDepartment = true;
        this.activeDepartmentId = null;
        form.reset();
        this.renderDeptFile(form, null);
        if (this.deptIdInput) this.deptIdInput.value = '';
        if (this.parentIdInput) this.parentIdInput.value = this.activeFacilityId;

        this.deptDetailContent?.querySelector('.department-empty')?.remove();

        this.departmentsList?.querySelectorAll('.department-item').forEach(el => {
            el.classList.remove('active');
        });

        this.setDeptMode('new');
        form.querySelector('[name="department_name"]')?.focus();
    }

    hideDepartmentForm() {
        const form = document.getElementById('ds_department_form');
        if (form) form.reset();
        this.activeDepartmentId = null;
        this._addingNewDepartment = false;
        this.setDeptMode('empty');
        if (this.deptDetailContent && !this.deptDetailContent.querySelector('.department-empty')) {
            const p = document.createElement('p');
            p.className = 'department-empty';
            p.textContent = 'Select a department to view details.';
            this.deptDetailContent.prepend(p);
        }
    }

    async handleSaveDepartment(e) {
        e.preventDefault();
        const form = document.getElementById('ds_department_form');
        if (!form) return;

        const formData = new FormData(form);
        formData.append('action', 'save_department');

        try {
            this.showSavePreloader();
            const data = await this.ajaxRaw(formData);
            this.activeDepartmentId = data.department_id;
            const deptIdInput = form.querySelector('[name="department_id"]');
            if (deptIdInput) deptIdInput.value = data.department_id;
            this._addingNewDepartment = false;
            this.renderDeptFile(form, data.file || null);
            if (data.file_error) {
                showToast(data.file_error, 'error', 8000);
            }
            await this.loadDepartments();
            this.facilitiesTable?.reset();
        } catch (err) {
            alert(err.message || 'Error saving department');
        } finally {
            this.hideSavePreloader();
        }
    }

    async ajax(action, params = {}) {
        const formData = new FormData();
        formData.append('action', action);
        formData.append('nonce', window.docuswiftPortal?.nonce || '');
        Object.entries(params).forEach(([k, v]) => formData.append(k, v));
        return this.ajaxRaw(formData);
    }

    async ajaxRaw(formData) {
        const response = await fetch(this.ajaxUrl, { method: 'POST', body: formData });
        const result = await response.json();
        if (!result.success) throw new Error(result.data?.message || 'Request failed');
        return result.data;
    }

    populateForm(form, data) {
        if (!form || !data) return;
        Object.entries(data).forEach(([key, value]) => {
            const val = value ?? '';
            const radios = form.querySelectorAll(`input[type="radio"][name="${key}"]`);
            if (radios.length) {
                radios.forEach((r) => {
                    r.checked = String(r.value) === String(val);
                });
                return;
            }
            const field = form.querySelector(`[name="${key}"]`);
            if (!field) return;
            // `file` inputs can't be populated programmatically for security,
            // and ACF file-type values are objects — skip them here and let the
            // caller render the current file via renderDeptFile().
            if (field.type === 'file' || (val && typeof val === 'object')) return;
            if (field.type === 'checkbox') {
                field.checked = Boolean(val && val !== '0' && val !== '');
            } else {
                field.value = val;
            }
        });
    }

    fadeIn(el) {
        el.style.display = 'block';
        el.style.opacity = 0;
        el.style.transition = 'opacity 0.2s';
        requestAnimationFrame(() => { el.style.opacity = 1; });
    }

    fadeOut(el) {
        el.style.opacity = 0;
        el.style.transition = 'opacity 0.2s';
        setTimeout(() => { el.style.display = 'none'; }, 200);
    }
}
